import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";

import ProjectAddress from "./ProjectAddress";
import ProjectBasicInfo from "./ProjectBasicInfo";
import ProjectRes from "./ProjectRes";
import ProjectsDetails from "./ProjectsDetails";

import FormDialog from "@/components/reusables/FormDialog";
import { Button } from "@/components/ui/button";
import * as Dialog from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { PROJECT_CONSTANTS } from "@/constants/projects.constant";
import { docketApi } from "@/redux/api/docketQuery";
import { useAddProjectMutation } from "@/redux/api/projectsQuery";
import { AddProjectSchema, TAddProjectSchema } from "@/schemas/project.schema";
import { customToast } from "@/utils/custom-toast";
import { toUTC } from "@/utils/helper-functions/date-time.helper";
import { validateResponse } from "@/utils/helper-functions/form.helper";

export const AddProject = ({
  isOpen,
  toggleModal,
  // refetch,
}: {
  isOpen: boolean;
  toggleModal: () => void;
  // refetch: () => void;
}): React.JSX.Element => {
  const dispatch = useDispatch();
  const form = useForm<TAddProjectSchema>({
    mode: "onChange",
    resolver: zodResolver(AddProjectSchema),
    defaultValues: {
      startDate: undefined,
      address: {
        latitude: "0",
        longitude: "0",
        label: "",
        areaLocation: "",
        radius: 200,
      },
      customerId: "",
    },
  });

  const { reset, handleSubmit } = form;

  const [addProject, { isLoading }] = useAddProjectMutation();

  const handleExit = (): void => {
    reset();
    toggleModal();
  };

  const onSubmit = async (data: TAddProjectSchema): Promise<void> => {
    let formattedData = {
      ...data,
    };

    if (data?.startDate) {
      formattedData = {
        ...formattedData,
        startDate: toUTC(data.startDate),
      };
    }

    const response = await addProject({
      data: formattedData,
    });

    validateResponse({
      response,
      onSuccess() {
        customToast.success(PROJECT_CONSTANTS.MESSAGES.ADDED);
        dispatch(docketApi.util.invalidateTags(["docket"]));
        handleExit();
      },
    });
  };

  const handleReset = (): void => {
    reset();
  };

  return (
    <FormDialog
      isOpen={isOpen}
      toggleModal={handleExit}
      title='Add New Project'
      isFormDirty={form.formState.isDirty}
      className='sm:max-w-lg md:max-w-5xl md:max-h-[96%] scrollbar-thin '
    >
      <Form {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className='grid grid-cols-1 gap-6 mt-4 text-xs lg:grid-cols-3'>
            <ProjectBasicInfo />
            <ProjectAddress />
            <ProjectsDetails />
            <ProjectRes />
          </div>
          <Dialog.DialogFooter className='flex items-center justify-between md:mt-8'>
            <div className='flex items-center justify-between w-full'>
              <Button
                className='rounded-sm'
                size={"lg"}
                type='button'
                variant={"ghost"}
                onClick={handleReset}
              >
                Reset
              </Button>

              <Button
                className='rounded-sm'
                size={"lg"}
                type='submit'
                isLoading={isLoading}
                message='Saving...'
              >
                SAVE PROJECT
              </Button>
            </div>
          </Dialog.DialogFooter>
        </form>
      </Form>
    </FormDialog>
  );
};
